import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import retailApi from '../api/retailApi';
import api from '../api/api';
import {
  clearLocalData,
  getAccessToken,
  getEffectiveRoles,
  getLocalData,
  removeAxiosHeaders,
  setAccessToken,
  setAxiosHeaders,
  setLocalData,
  setRefreshToken,
  setRetailAccessToken,
  setRetailRefreshToken,
} from '../utils/helper';
import { identify, setUserProperties, trackData } from '../utils/trackers';

const isUserLoggedIn = () => {
  const accessToken = getAccessToken();
  return accessToken ? true : false;
};

const fetchCartCount = createAsyncThunk('auth/fetchCartCount', (data: any) => {
  return retailApi.cart
    .cartCount({
      slug: data?.slug,
      organisation_id: data?.organisation_id,
    })
    .then((res) => res.data.items_count);
});

export const fetchUserDetails = createAsyncThunk('auth/fetchUserDetails', async () => {
  const response = await api.profile.userData();
  return response;
});

interface TAuthState {
  id: number | null;
  uuid: number | null;
  member_id: number | string | null;
  member_uuid: string | null;
  status: string | null;
  isLoggedIn: boolean;
  roles: Array<string>;
  name: string;
  email: string;
  organisation_id: number | null;
  organisation_uuid: string | null;
  activeMode: string;
  relationship_managers: object;
  uhi: object;
  notification_count: number;
  phone: string | null;
  cartCount: any;
  is_wellness_only: boolean;
}

const initialState: TAuthState = {
  id: null,
  uuid: null,
  member_id: null,
  member_uuid: null,
  isLoggedIn: isUserLoggedIn(),
  roles: [],
  status: null,
  name: '',
  email: '',
  organisation_id: null,
  organisation_uuid: getLocalData('chosenEntity')?.organisation_uuid || null,
  activeMode: getLocalData('activeMode') || 'employee',
  relationship_managers: {},
  uhi: {},
  notification_count: 0,
  phone: null,
  cartCount: 0,
  is_wellness_only: false,
};

const updateRolesAndMode = (state: TAuthState, payload: any) => {
  const effectiveRoles = getEffectiveRoles(payload);
  if (effectiveRoles.length > 0) {
    state.roles = effectiveRoles;
  } else if (payload?.roles) {
    state.roles = Array.isArray(payload.roles) ? payload.roles : [payload.roles];
  } else {
    state.roles = [];
  }

  const isEmployer = state.roles?.includes?.('employer');
  if (!isEmployer && state.activeMode === 'employer') {
    state.activeMode = 'employee';
    setLocalData('activeMode', 'employee');
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action) => {
      state.isLoggedIn = true;
      setAccessToken(action.payload.accessToken);
      setRetailAccessToken(action.payload.accessToken);
      setRefreshToken(action.payload.refreshToken);
      setRetailRefreshToken(action.payload.refreshToken);
    },
    retailLogin: (state, action) => {
      setRetailAccessToken(action.payload.accessToken);
      setRetailRefreshToken(action.payload.refreshToken);
      setAxiosHeaders();
      state.isLoggedIn = true;
      state.phone = action.payload?.phone;
    },
    retailLogout: (state) => {
      clearLocalData();
      removeAxiosHeaders();
      return { ...initialState, isLoggedIn: false };
    },
    userData: (state, action) => {
      identify(action.payload.id);
      setUserProperties({
        ID: action.payload?.id,
        'Member ID': action.payload?.member_id,
        $name: action.payload.name,
        $email: action.payload.email,
        $phone: action.payload.phone,
        'Organisation ID': action.payload.organisation_id,
        Roles: action.payload.roles,
      });
      state.uuid = action.payload?.uuid;
      state.id = action.payload?.id;
      state.member_id = action.payload?.member_id;
      state.member_uuid = action.payload?.member_uuid;

      updateRolesAndMode(state, action.payload);

      state.name = action.payload?.name;
      state.email = action.payload?.email;
      state.phone = action.payload?.phone;
      state.status = action.payload?.status;
      state.organisation_id = action.payload?.organisation_id;
      state.organisation_uuid = action.payload?.organisation_uuid;
      state.relationship_managers = action.payload?.relationship_managers;
      state.notification_count = action.payload.notification_count;
    },
    setIsWellnessOnly: (state, action) => {
      state.is_wellness_only = action.payload;
    },
    setUHI: (state, action) => {
      state.uhi = action.payload?.uhi;
    },
    setRoles: (state, action) => {
      updateRolesAndMode(state, action.payload);
    },
    setActiveMode: (state, action) => {
      const isEmployer = state.roles?.includes?.('employer');
      if (action.payload === 'employer' && !isEmployer) {
        state.activeMode = 'employee';
        setLocalData('activeMode', 'employee');
      } else {
        state.activeMode = action.payload;
      }
    },
    setNotification: (state, action) => {
      state.notification_count = action.payload.notification_count;
    },
    setName: (state, action) => {
      state.name = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    changeActiveMode: (state, action) => {
      const isEmployer = state.roles?.includes?.('employer');
      if (action.payload === 'employer' && !isEmployer) {
        state.activeMode = 'employee';
        setLocalData('activeMode', 'employee');
      } else {
        state.activeMode = action.payload;
        setLocalData('activeMode', action.payload);
      }
    },
    setOrganisationUuid: (state, action) => {
      state.organisation_uuid = action.payload;
    },
    logout: (state) => {
      clearLocalData();
      removeAxiosHeaders();
      trackData('Logout');
      return { ...initialState, isLoggedIn: false, activeMode: 'employee' };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCartCount.fulfilled, (state, action) => {
      state.cartCount = action.payload;
    });
    builder.addCase(fetchUserDetails.fulfilled, (state, action) => {
      const response = action.payload;
      identify(response.id);
      setUserProperties({
        ID: response?.id,
        'Member ID': response?.member_id,
        $name: response.full_name || response.first_name,
        $email: response.email,
        $phone: response.phone,
        'Organisation ID': response.organisation_id,
        Roles: response.roles,
      });
      state.uuid = response?.uuid;
      state.id = response?.id;
      state.member_id = response?.member_id;
      state.member_uuid = response?.member_uuid;

      updateRolesAndMode(state, response);

      state.name = response.full_name || response.first_name;
      state.email = response?.email;
      state.phone = response?.phone;
      state.status = response?.status;
      state.organisation_id = response?.organisation_id;
      state.organisation_uuid = response?.organisation_uuid;
      state.relationship_managers = response?.relationship_manager;
    });
  },
});

export const authActions = authSlice.actions;
export const updateCartCount = fetchCartCount;
export default authSlice.reducer;
